import { createContext, Dispatch, PropsWithChildren, useCallback, useEffect, useReducer } from 'react';
import { ThemeName } from './theme.types';
import { useMediaQuery } from 'es/app/theme/useMediaQuery';
import { useLocalStorage } from 'react-use';
import { THEME_NAMES } from 'es/app/theme/theme.constants';
import * as actionCreators from './actions';
import { Classes } from '@blueprintjs/core';

type ThemeAction = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

type ThemeState = {
    themeName: ThemeName;
};

type ThemeContext = ThemeState & { dispatch: Dispatch<ThemeAction> };

const LOCAL_STORAGE_KEY_THEME_NAME = 'theme_name';
const LIGHT_COLOUR_SCHEME_MEDIA_QUERY = '(prefers-color-scheme: light)';
const THEME_NAME_CLASSES = THEME_NAMES.map(name => `theme-${name}`);

const INITIAL_STATE: ThemeState = {
    themeName: 'light',
};

export const THEME_CONTEXT = createContext<ThemeContext | null>(null);
THEME_CONTEXT.displayName = 'Theme';

const { Provider } = THEME_CONTEXT;

export const ThemeProvider = ({ children }: PropsWithChildren<Record<never, never>>) => {
    const { storedThemeName, setStoredThemeName } = useStoredThemeName();
    const [state, dispatch] = useReducer(themeReducer, {
        ...INITIAL_STATE,
        themeName: storedThemeName,
    });
    const handleThemeNameChange = useCallback(
        (themeName: ThemeName) => {
            setStoredThemeName(themeName);
            const [existingThemeName] = THEME_NAME_CLASSES.filter(className => !className.includes(themeName));
            const newThemeName = THEME_NAME_CLASSES.find(className => className.includes(themeName));
            if (existingThemeName) {
                document.body.classList.remove(existingThemeName);
            }
            if (newThemeName) {
                document.body.classList.add(newThemeName);
                // Set the theme name as data-bs-theme attribute for Bootstrap
                document.body.setAttribute('data-bs-theme', themeName);
            }
            if (themeName === 'dark') {
                document.body.classList.add(Classes.DARK);
            } else {
                document.body.classList.remove(Classes.DARK);
            }
        },
        [setStoredThemeName]
    );

    const { themeName } = state;

    useEffect(() => {
        handleThemeNameChange(themeName);
    }, [themeName, handleThemeNameChange]);

    return (
        <Provider
            value={{
                ...state,
                dispatch,
            }}
        >
            {children}
        </Provider>
    );
};

function themeReducer(state: ThemeState, action: ThemeAction): ThemeState {
    switch (action.type) {
        case 'SET_THEME_NAME': {
            return {
                ...state,
                themeName: action.payload,
            };
        }
        default: {
            throw new Error(`Unknown action:\n${JSON.stringify(action, null, 2)}`);
        }
    }
}

function useStoredThemeName() {
    const prefersLight = useMediaQuery(LIGHT_COLOUR_SCHEME_MEDIA_QUERY);
    const initialValue = prefersLight ? 'light' : 'dark';
    const [localStorageValue, setLocalStorageValue] = useLocalStorage(LOCAL_STORAGE_KEY_THEME_NAME, initialValue);
    const storedThemeName: ThemeName = THEME_NAMES.find(v => v === localStorageValue) ?? 'light';
    return { storedThemeName, setStoredThemeName: setLocalStorageValue };
}
